/**
 * **La puerta por la que pasa todo lo que genera la IA.**
 *
 * Los validadores reales de este repo son dos, viven en TypeScript y son
 * distintos entre sí — esto es lo que corrige el enunciado del plan:
 *
 * | Qué se valida | Con qué | Dónde |
 * |---|---|---|
 * | La **definición de un bloque** (`quiz`, `flashcard`, …) | `BlockPlugin.validate(def)` | `learning-blocks/types.ts` |
 * | La **config de un módulo** (`lesson`, `resource`, `assignment`, …) | `CourseModulePlugin.configSchema.parse(config)` | `academy/modules/types.ts` |
 *
 * No son lo mismo ni viven en el mismo registro: los bloques **no** tienen
 * `configSchema`. Hay que pasar por los dos, cada uno en el suyo, y eso es lo
 * que hace [`validateProposalItem`].
 *
 * Por qué aquí y no en Rust: el generador es Rust, pero reimplementar estos
 * validadores allí crearía una **segunda fuente de verdad** que se desincroniza
 * en silencio la primera vez que alguien registre un plugin nuevo, y el motor de
 * bloques dejaría de ser extensible. El servidor pone una red estructural barata
 * (tipo conocido, campos presentes, ids que existen en el curso) y deja los
 * bloques en `valid: false` a propósito: sin este archivo, nada se aprueba.
 */

import { validateBlock } from "../../learning-blocks/engine";
import type { BlockRegistry } from "../../learning-blocks/registry";
import type { CourseModuleRegistry } from "../modules/registry";
import { isPlainObject } from "../modules/types";
import type { AiProposalItem, AiProposalKind } from "./contract";

/** Veredicto sobre una pieza. `valid: false` ⇒ no se puede aprobar. */
export interface ItemVerdict {
  valid: boolean;
  errors: string[];
}

export interface ValidationContext {
  blocks: BlockRegistry;
  modules: CourseModuleRegistry;
  /**
   * Kinds habilitados en ESTE curso (`registry.enabledKinds(...)`). Un kind
   * registrado pero apagado en `course_settings.enabled_module_kinds` no puede
   * aprobarse: generar con IA no es un permiso para saltarse un ajuste.
   */
  enabledModuleKinds: readonly string[];
}

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Valida una pieza de bloque contra `BlockPlugin.validate`.
 *
 * Nota deliberada: se valida la `def`, **no** con `configSchema`. Un bloque no
 * tiene `configSchema`; confundirlos es el error que este archivo existe para
 * no cometer.
 */
function validateBlockItem(item: AiProposalItem, ctx: ValidationContext): ItemVerdict {
  const { payload } = item;
  const type = str(payload.type);
  if (!type) {
    return { valid: false, errors: ["el bloque propuesto no declara su tipo"] };
  }
  if (!ctx.blocks.has(type)) {
    return { valid: false, errors: [`tipo de bloque desconocido: «${type}»`] };
  }
  const { def } = payload;
  if (!isPlainObject(def)) {
    return { valid: false, errors: ["la definición del bloque no es un objeto"] };
  }
  const result = validateBlock(ctx.blocks, { id: item.id, type, def });
  return result.ok ? { valid: true, errors: [] } : { valid: false, errors: result.errors };
}

/**
 * Valida una pieza de módulo contra `CourseModulePlugin.configSchema` — el OTRO
 * registro — y contra los kinds realmente habilitados en el curso.
 */
function validateModuleItem(item: AiProposalItem, ctx: ValidationContext): ItemVerdict {
  const { payload } = item;
  const kind = str(payload.kind);
  const errors: string[] = [];
  if (!kind) {
    return { valid: false, errors: ["el módulo propuesto no declara su tipo"] };
  }
  const plugin = ctx.modules.get(kind);
  if (!plugin) {
    return { valid: false, errors: [`tipo de módulo desconocido: «${kind}»`] };
  }
  if (!ctx.enabledModuleKinds.includes(kind)) {
    errors.push(`«${kind}» no está habilitado en este curso`);
  }
  if (!str(payload.title)) {
    errors.push("el módulo propuesto no tiene título");
  }
  const parsed = plugin.configSchema.parse(payload.config ?? {});
  if (!parsed.ok) {
    errors.push(...parsed.errors);
  }
  return { valid: errors.length === 0, errors };
}

function validateUnitItem(item: AiProposalItem): ItemVerdict {
  return str(item.payload.title)
    ? { valid: true, errors: [] }
    : { valid: false, errors: ["la unidad propuesta no tiene título"] };
}

function validateCourseItem(item: AiProposalItem): ItemVerdict {
  return str(item.payload.title)
    ? { valid: true, errors: [] }
    : { valid: false, errors: ["el curso propuesto no tiene título"] };
}

/**
 * Una rúbrica generada: criterios observables con nombre y descripción. Es la
 * forma que consume `render_rubric` en el servidor; si cambia allí, cambia aquí.
 */
function validateRubricItem(item: AiProposalItem): ItemVerdict {
  const { criteria } = item.payload;
  if (!Array.isArray(criteria) || criteria.length === 0) {
    return { valid: false, errors: ["la rúbrica no tiene criterios"] };
  }
  const errors: string[] = [];
  criteria.forEach((c, i) => {
    if (!isPlainObject(c)) {
      errors.push(`el criterio ${i + 1} no es un objeto`);
      return;
    }
    if (!str(c.name)) {
      errors.push(`el criterio ${i + 1} no tiene nombre`);
    }
    if (!str(c.description)) {
      errors.push(`el criterio «${str(c.name) || i + 1}» no dice qué hay que ver en la entrega`);
    }
  });
  return { valid: errors.length === 0, errors };
}

/**
 * Veredicto sobre UNA pieza, cruzando el registro que le toca.
 *
 * Regla que no se negocia: **un borrado nunca se auto-valida**. Es la operación
 * que más caro sale equivocarse y siempre la aprueba una persona a mano.
 */
export function validateProposalItem(item: AiProposalItem, ctx: ValidationContext): ItemVerdict {
  if (item.op === "delete") {
    return {
      valid: false,
      errors: ["un borrado propuesto por la IA lo confirma una persona, siempre"],
    };
  }
  switch (item.entity) {
    case "block": {
      return validateBlockItem(item, ctx);
    }
    case "course_module": {
      return validateModuleItem(item, ctx);
    }
    case "unit": {
      return validateUnitItem(item);
    }
    case "course": {
      return validateCourseItem(item);
    }
    case "rubric": {
      return validateRubricItem(item);
    }
    default: {
      // Un tipo que este archivo no sabe juzgar NO se aprueba solo. Devolver
      // `true` por defecto sería el fallo silencioso más caro de la feature.
      return {
        valid: false,
        errors: [`«${item.entity}» necesita revisión manual antes de aplicarse`],
      };
    }
  }
}

/** Veredicto de todas las piezas, indexado por id. */
export function validateProposalItems(
  items: readonly AiProposalItem[],
  ctx: ValidationContext,
): Map<string, ItemVerdict> {
  return new Map(items.map((i) => [i.id, validateProposalItem(i, ctx)]));
}

/** Resumen para la cabecera del panel de revisión. */
export interface ProposalReviewSummary {
  total: number;
  valid: number;
  invalid: number;
  applied: number;
  rejected: number;
  /** ¿Queda algo que se pueda aprobar? */
  approvable: number;
}

export function summarizeReview(
  items: readonly AiProposalItem[],
  verdicts: ReadonlyMap<string, ItemVerdict>,
): ProposalReviewSummary {
  let valid = 0;
  let approvable = 0;
  let applied = 0;
  let rejected = 0;
  for (const item of items) {
    const v = verdicts.get(item.id);
    if (v?.valid) {
      valid += 1;
      if (item.status === "pending" || item.status === "approved") {
        approvable += 1;
      }
    }
    if (item.status === "applied") {
      applied += 1;
    }
    if (item.status === "rejected") {
      rejected += 1;
    }
  }
  return {
    total: items.length,
    valid,
    invalid: items.length - valid,
    applied,
    rejected,
    approvable,
  };
}

/** Piezas de una propuesta, en el orden en que hay que aplicarlas. */
const APPLY_ORDER: readonly string[] = ["course", "unit", "lesson", "course_module", "block"];

/**
 * Ordena las piezas por dependencia: la unidad antes que su módulo, el módulo
 * antes que su bloque. Aplicarlas en el orden en que las devolvió el modelo
 * dejaría hijos huérfanos apuntando a padres que aún no existen.
 */
export function sortForApply(items: readonly AiProposalItem[]): AiProposalItem[] {
  const rank = (e: string) => {
    const i = APPLY_ORDER.indexOf(e);
    return i === -1 ? APPLY_ORDER.length : i;
  };
  return items.toSorted((a, b) => rank(a.entity) - rank(b.entity) || a.path.localeCompare(b.path));
}

/** ¿Esta clase de propuesta puede aplicarse al árbol de un curso? */
export function isApplicableKind(kind: AiProposalKind): boolean {
  return kind === "structure" || kind === "blocks" || kind === "edit";
}
